import { JsonSchemaField } from '../types/Knowledge';

export type ToolTemplateId = 'lookup' | 'search' | 'browse' | '';

export interface ToolTemplateSettings {
  kernelFunctionName: string;
  toolDisplayName: string;
  toolDescription: string;
  maxResults: number;
  sortJsonPath: string;
  sortDirection: 'asc' | 'desc';
  responseInstruction: string;
}

export interface ToolTemplateParameterPatch {
  jsonPath: string;
  selected: boolean;
  matchMode: string;
  isRequired: boolean;
}

const toFunctionPrefix = (displayName: string): string =>
  displayName
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('')
    .slice(0, 24) || 'Data';

const findDateField = (fields: JsonSchemaField[]): JsonSchemaField | undefined =>
  fields.find(field =>
    field.type === 'string'
    && (field.jsonPath.toLowerCase().includes('date') || field.name.toLowerCase().includes('date')));

const findIdentifierField = (fields: JsonSchemaField[]): JsonSchemaField | undefined =>
  fields.find(field =>
    field.jsonPath.toLowerCase().includes('id')
    || field.name.toLowerCase().includes('id')
    || field.name.toLowerCase().includes('code'))
  ?? fields[0];

export const buildToolTemplateSettings = (
  templateId: ToolTemplateId,
  categoryDisplayName: string,
  fields: JsonSchemaField[],
): ToolTemplateSettings | null => {
  if (!templateId) {
    return null;
  }

  const prefix = toFunctionPrefix(categoryDisplayName);
  const dateField = findDateField(fields);

  if (templateId === 'lookup') {
    return {
      kernelFunctionName: `Lookup${prefix}`,
      toolDisplayName: `Lookup ${categoryDisplayName}`,
      toolDescription: `Find a specific ${categoryDisplayName.toLowerCase()} record using exact identifier filters.`,
      maxResults: 5,
      sortJsonPath: dateField?.jsonPath ?? '',
      sortDirection: 'asc',
      responseInstruction: 'Use the returned record to answer precisely. If no record matches, say so clearly.',
    };
  }

  if (templateId === 'search') {
    return {
      kernelFunctionName: `Search${prefix}`,
      toolDisplayName: `Search ${categoryDisplayName}`,
      toolDescription: `Search ${categoryDisplayName.toLowerCase()} records using flexible text filters.`,
      maxResults: 50,
      sortJsonPath: dateField?.jsonPath ?? '',
      sortDirection: dateField ? 'desc' : 'asc',
      responseInstruction: 'Summarize the matching records briefly and highlight the most relevant rows for the user question.',
    };
  }

  return {
    kernelFunctionName: `List${prefix}`,
    toolDisplayName: `List ${categoryDisplayName}`,
    toolDescription: `List recent ${categoryDisplayName.toLowerCase()} records for overview questions.`,
    maxResults: 20,
    sortJsonPath: dateField?.jsonPath ?? '',
    sortDirection: dateField ? 'desc' : 'asc',
    responseInstruction: 'Present the returned rows as a concise list and mention when results were truncated.',
  };
};

export const buildToolTemplateParameterPatches = (
  templateId: ToolTemplateId,
  fields: JsonSchemaField[],
): ToolTemplateParameterPatch[] => {
  if (!templateId || fields.length === 0) {
    return fields.map(field => ({
      jsonPath: field.jsonPath,
      selected: true,
      matchMode: 'contains',
      isRequired: false,
    }));
  }

  const dateField = findDateField(fields);
  const identifierField = findIdentifierField(fields);
  const primaryFields = fields.slice(0, 3);

  return fields.map((field) => {
    if (templateId === 'lookup') {
      const isPrimary = field.jsonPath === identifierField?.jsonPath;
      return {
        jsonPath: field.jsonPath,
        selected: isPrimary,
        matchMode: isPrimary ? 'equals' : 'contains',
        isRequired: isPrimary,
      };
    }

    if (templateId === 'search') {
      const isPrimary = primaryFields.some(item => item.jsonPath === field.jsonPath);
      return {
        jsonPath: field.jsonPath,
        selected: isPrimary,
        matchMode: field.type === 'number' ? 'numericgte' : 'contains',
        isRequired: false,
      };
    }

    const isOptionalFilter = field.jsonPath === primaryFields[0]?.jsonPath;
    return {
      jsonPath: field.jsonPath,
      selected: isOptionalFilter,
      matchMode: field.jsonPath === dateField?.jsonPath ? 'dateafter' : 'contains',
      isRequired: false,
    };
  });
};

export const buildToolTemplateResponseFieldPaths = (
  templateId: ToolTemplateId,
  fields: JsonSchemaField[],
): string[] => {
  if (templateId === 'lookup' || fields.length <= 4) {
    return [];
  }

  return fields.slice(0, 4).map(field => field.jsonPath);
};
